import React, { useState, useEffect, useRef } from 'react';
import WebcamFeed from '../components/WebcamFeed';
import GameHUD from '../components/GameHUD';
import FeedbackOverlay from '../components/FeedbackOverlay';
import { detectPunch } from '../utils/poseCalculations';
import { Camera, User } from 'lucide-react';

const CombatGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [timer, setTimer] = useState(60);
  const [restTime, setRestTime] = useState(10);
  const [isResting, setIsResting] = useState(true);
  const [round, setRound] = useState(1);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [targets, setTargets] = useState([]);
  const [hitTargets, setHitTargets] = useState([]);
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);
  const [viewMode, setViewMode] = useState('camera');
  const previousPoseRef = useRef(null);
  const lastPunchTimeRef = useRef(0);
  const totalMovesRef = useRef(0);
  const correctMovesRef = useRef(0);
  const audioContextRef = useRef(null);
  const musicAudioRef = useRef(null);
  const scoreRef = useRef(score);
  const accuracyRef = useRef(accuracy);
  const onGameEndRef = useRef(onGameEnd);
  scoreRef.current = score;
  accuracyRef.current = accuracy;
  onGameEndRef.current = onGameEnd;

  useEffect(() => {
    // Start music once on first GET READY countdown
    if (isResting && round === 1) {
      playBackgroundMusic();
    }

    // Rest period countdown
    if (isResting) {
      const restInterval = setInterval(() => {
        setRestTime((prev) => {
          if (prev <= 1) {
            clearInterval(restInterval);
            setIsResting(false);
            setTimer(60);
            return 20;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(restInterval);
    }

    // Game timer countdown
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          if (round === 1) {
            // After first round, take rest and start round 2
            setIsResting(true);
            setRound(2);
            setRestTime(20);
          } else {
            // After round 2, end game
            stopBackgroundMusic();
            onGameEndRef.current?.({
              game_type: 'combat',
              score: scoreRef.current,
              accuracy: accuracyRef.current,
              duration: 120
            });
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    generateTargets();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResting, round]);

  const generateTargets = () => {
    const positions = [
      { x: 15, y: 20 },
      { x: 40, y: 15 },
      { x: 60, y: 20 },
      { x: 85, y: 15 }
    ];
    
    const newTargets = positions.map((pos, i) => ({
      id: Date.now() + i,
      x: pos.x,
      y: pos.y,
      side: i % 2 === 0 ? 'left' : 'right'
    }));
    
    setTargets(newTargets);
  };

  const playBackgroundMusic = () => {
    try {
      if (!musicAudioRef.current) {
        musicAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/shashti.mp3`);
        musicAudioRef.current.volume = 0.3;
        musicAudioRef.current.addEventListener('ended', () => {
          musicAudioRef.current.currentTime = 50;
          musicAudioRef.current.play();
        });
      }

      musicAudioRef.current.currentTime = 50;
      const playPromise = musicAudioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('Music started playing');
          setShowMusicPrompt(false);
        }).catch(error => {
          console.log('Autoplay prevented, showing music prompt');
          setShowMusicPrompt(true);
        });
      }
    } catch (error) {
      console.log('Failed to play music:', error);
      setShowMusicPrompt(true);
    }
  };

  const enableMusic = () => {
    if (musicAudioRef.current) {
      musicAudioRef.current.play().then(() => {
        setShowMusicPrompt(false);
      }).catch(error => {
        console.log('Failed to play music:', error);
      });
    }
  };

  const stopBackgroundMusic = () => {
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      musicAudioRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    return () => {
      stopBackgroundMusic();
    };
  }, []);

  const playBeep = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 800;
    gainNode.gain.value = 0.1;
    
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.1);
  };

  const handlePoseUpdate = (landmarks) => {
    const now = Date.now();
    if (now - lastPunchTimeRef.current < 300) return;

    const punchResult = detectPunch(landmarks);

    if (punchResult.detected) {
      lastPunchTimeRef.current = now;
      totalMovesRef.current++;
      correctMovesRef.current++;

      const points = Math.floor(punchResult.power * 100) + (combo * 10);
      setScore((prev) => prev + points);
      setCombo((prev) => prev + 1);
      
      const newAccuracy = (correctMovesRef.current / totalMovesRef.current) * 100;
      setAccuracy(newAccuracy);

      // Add hit effect
      const hitId = `hit-${now}`;
      setHitTargets(prev => [...prev, hitId]);
      setTimeout(() => {
        setHitTargets(prev => prev.filter(id => id !== hitId));
      }, 500);

      if (combo >= 10) {
        setFeedback('perfect');
      } else if (combo >= 5) {
        setFeedback('excellent');
      } else {
        setFeedback('good');
      }
      
      setShowFeedback(true);
      playBeep();
      
      setTimeout(() => setShowFeedback(false), 500);
      setTimeout(() => setCombo(0), 2000);
    }

    previousPoseRef.current = landmarks;
  };

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden">
      <WebcamFeed onPoseUpdate={handlePoseUpdate} isActive={!isResting} cameraOpacity={0.3} viewMode={viewMode} />
      
      {isResting ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md">
          <div className="text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4 animate-pulse">💪</div>
              <h2 className="font-heading text-4xl font-black uppercase text-primary mb-2">
                {round === 1 ? 'GET READY!' : 'REST TIME!'}
              </h2>
              <p className="text-muted-foreground font-body text-lg uppercase tracking-wider">
                {round === 1 ? 'Prepare for Combat Cardio' : 'Round 1 Complete! Get Ready for Round 2'}
              </p>
            </div>
            
            <div className="bg-muted/20 rounded-xl p-8 backdrop-blur-xl border border-primary/30">
              <div className="text-7xl font-heading font-black text-accent mb-2">
                0:{restTime.toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Rest Time</div>
            </div>

            {round === 1 && (
              <div className="mt-8 space-y-3 text-left max-w-md mx-auto">
                <div className="flex items-center gap-3 text-foreground">
                  <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
                    <span className="text-secondary">👊</span>
                  </div>
                  <span className="font-body">Punch the pink targets when they appear</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    <span className="text-accent">⚡</span>
                  </div>
                  <span className="font-body">Build combos for higher scores</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary">🎯</span>
                  </div>
                  <span className="font-body">2 rounds × 1 minute each</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur-xl border border-accent/50 rounded-lg px-6 py-2 z-30">
            <div className="text-accent font-heading text-lg uppercase tracking-wider">
              ROUND {round} / 2
            </div>
          </div>

          <div className="absolute top-4 left-4 z-30">
            <button
              onClick={() => setViewMode(prev => prev === 'camera' ? 'avatar' : 'camera')}
              className="flex items-center gap-2 bg-black/70 backdrop-blur-xl border border-white/20 rounded-lg px-4 py-2 transition-all duration-200 hover:border-primary/50 hover:bg-black/80 active:scale-95"
            >
              {viewMode === 'avatar' ? (
                <>
                  <User className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#00E5FF]">Avatar</span>
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 text-primary" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-primary">Camera</span>
                </>
              )}
            </button>
          </div>

          {targets.map((target) => {
            const isHit = hitTargets.length > 0;
            return (
              <div
                key={target.id}
                className={`absolute w-24 h-24 rounded-full border-4 transition-all duration-200 ${
                  isHit 
                    ? 'border-accent bg-accent/50 scale-125' 
                    : 'border-secondary animate-pulse-neon'
                }`}
                style={{
                  left: `${target.x}%`,
                  top: `${target.y}%`,
                  transform: 'translate(-50%, -50%)',
                  background: isHit 
                    ? 'radial-gradient(circle, rgba(57,255,20,0.6), transparent)'
                    : 'radial-gradient(circle, rgba(255,0,153,0.3), transparent)',
                  boxShadow: isHit
                    ? '0 0 30px rgba(57,255,20,0.8), 0 0 60px rgba(57,255,20,0.5)'
                    : '0 0 20px rgba(255,0,153,0.5)'
                }}
                data-testid="combat-target"
              >
                <div className={`absolute inset-0 flex items-center justify-center font-heading font-bold text-sm ${
                  isHit ? 'text-black' : 'text-secondary'
                }`}>
                  {isHit ? 'HIT!' : 'PUNCH'}
                </div>
              </div>
            );
          })}

          <GameHUD score={score} combo={combo} accuracy={accuracy} timer={timer} />
          <FeedbackOverlay feedback={feedback} visible={showFeedback} />

          {showMusicPrompt && (
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-xl border border-accent/50 rounded-lg px-6 py-3 animate-pulse-neon">
              <button
                onClick={enableMusic}
                className="flex items-center gap-2 text-accent font-heading text-sm uppercase tracking-wider"
                data-testid="enable-music-button"
              >
                <span>🎵</span>
                <span>Click to Enable Music</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CombatGame;
